import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { v: "podcast", l: "Podcast" }, { v: "audio", l: "Audio" }, { v: "reflection", l: "Reflection" },
  { v: "prompt", l: "Prompt" }, { v: "question", l: "Question" },
];

export default function AdminContent() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "podcast", title: "", description: "", body: "", audio_url: "", chapter: "" });

  const load = () => api.get("/contents").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) return toast.error("The title is required.");
    try {
      await api.post("/contents", form);
      setForm({ type: "podcast", title: "", description: "", body: "", audio_url: "", chapter: "" });
      setOpen(false);
      toast.success("Content published.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/contents/${id}`); toast.success("Removed."); load(); } catch {}
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-heading text-3xl tracking-tight">Content & Podcasts</h1>
        <Button className="btn-gold rounded-full text-sm font-medium" onClick={() => setOpen(!open)} data-testid="new-content-btn">
          <Plus className="w-4 h-4 mr-1" /> New
        </Button>
      </div>
      <p className="text-muted-foreground mb-8 text-sm">Publish podcasts, reflections and prompts linked to the chapters of the book.</p>

      {open && (
        <div className="bg-card border border-border rounded-2xl p-6 mb-8 space-y-4 animate-fade-in-up">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="mt-2 rounded-xl" data-testid="content-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Chapter (optional)</Label>
              <Input className="mt-2 rounded-xl" value={form.chapter} onChange={(e) => setForm({ ...form, chapter: e.target.value })} data-testid="content-chapter-input" />
            </div>
          </div>
          <div>
            <Label>Title</Label>
            <Input className="mt-2 rounded-xl" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="content-title-input" />
          </div>
          <div>
            <Label>Audio URL (optional)</Label>
            <Input className="mt-2 rounded-xl" placeholder="https://…" value={form.audio_url} onChange={(e) => setForm({ ...form, audio_url: e.target.value })} data-testid="content-audio-input" />
          </div>
          <div>
            <Label>Short description</Label>
            <Input className="mt-2 rounded-xl" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="content-desc-input" />
          </div>
          <div>
            <Label>Text / reflection</Label>
            <Textarea className="mt-2 rounded-xl min-h-28" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="content-body-input" />
          </div>
          <Button className="btn-gold rounded-full text-sm font-medium" onClick={create} data-testid="content-submit-btn">Publish</Button>
        </div>
      )}

      <div className="space-y-3" data-testid="admin-contents-list">
        {items.map((c) => (
          <div key={c.id} className="bg-card border border-border rounded-2xl p-5 flex items-center justify-between">
            <div>
              <span className="text-xs uppercase tracking-widest text-accent">{c.type}</span>
              <p className="font-heading text-lg">{c.title}</p>
            </div>
            <button onClick={() => remove(c.id)} className="p-2 text-muted-foreground hover:text-destructive transition-colors" data-testid={`delete-content-${c.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
